<template>
  <component :is="tag" :class="['heading-block', `heading-h${block.level}`]">
    <span v-if="block.level <= 2" class="heading-accent" aria-hidden="true" />
    <span class="heading-text">{{ block.text }}</span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface HeadingBlock {
  type: 'heading';
  level: 1 | 2 | 3;
  text: string;
}

const props = defineProps<{ block: HeadingBlock }>();

const tag = computed(() => {
  if (props.block.level === 1) return 'h1';
  if (props.block.level === 2) return 'h2';
  return 'h3';
});
</script>

<style scoped lang="scss">
.heading-block {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--oa-text-primary);
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
  scroll-margin-top: 80px;
}

.heading-accent {
  display: inline-block;
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(
    180deg,
    var(--oa-gold-full) 0%,
    var(--oa-gold-dim) 100%
  );
  flex-shrink: 0;
  align-self: stretch;
}

.heading-h1 {
  font-size: var(--oa-text-2xl, 28px);
  margin-top: 40px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid color-mix(in srgb, var(--oa-gold-full) 22%, transparent);

  .heading-accent {
    min-height: 32px;
  }
}

.heading-h2 {
  font-size: var(--oa-text-xl);
  margin-top: 36px;
  margin-bottom: 16px;

  .heading-accent {
    min-height: 24px;
  }
}

.heading-h3 {
  font-size: var(--oa-text-lg);
  margin-top: 28px;
  margin-bottom: 12px;
  color: var(--oa-gold-full);
  letter-spacing: 0.01em;

  &::before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 2px;
    background: var(--oa-gold-full);
    margin-right: 4px;
    flex-shrink: 0;
  }
}

.heading-block:first-child {
  margin-top: 0;
}
</style>
